import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  Search, GraduationCap, MapPin, DollarSign, TrendingUp, Users, 
  Check, AlertTriangle, Lock, Unlock, ChevronRight, Loader2,
  Target, Award, BookOpen, Building
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import debounce from 'lodash/debounce';
import { showFriendlyError } from '../utils/friendlyErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format currency
const formatCurrency = (amount) => {
  if (!amount) return 'N/A';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
};

// Format percentage
const formatPercent = (value) => {
  if (!value && value !== 0) return 'N/A';
  return `${(value * 100).toFixed(0)}%`;
};

// College Search Result Card
const CollegeSearchCard = ({ college, onSelect, isSelected }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      onClick={() => onSelect(college)}
      className={`p-4 rounded-xl border cursor-pointer transition-all ${
        isSelected 
          ? 'border-blue-500 bg-blue-500/20' 
          : 'border-slate-700 hover:border-slate-600 bg-slate-800/50'
      }`}
      data-testid={`college-result-${college.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h4 className="text-white font-semibold flex items-center gap-2">
            <GraduationCap className="w-4 h-4 text-blue-400" />
            {college.name}
          </h4>
          <p className="text-slate-300 text-sm flex items-center gap-1 mt-1">
            <MapPin className="w-3 h-3" />
            {college.city}, {college.state}
          </p>
        </div>
        {isSelected && (
          <Check className="w-5 h-5 text-blue-400" />
        )}
      </div>
      
      <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
        <div className="bg-slate-700/50 rounded-lg p-2">
          <div className="text-slate-300">Admission</div>
          <div className="text-white font-medium">{formatPercent(college.admission_rate)}</div>
        </div>
        <div className="bg-slate-700/50 rounded-lg p-2">
          <div className="text-slate-300">SAT Avg</div>
          <div className="text-white font-medium">{college.sat_avg || 'N/A'}</div>
        </div>
        <div className="bg-slate-700/50 rounded-lg p-2">
          <div className="text-slate-300">Tuition</div>
          <div className="text-white font-medium text-xs">{formatCurrency(college.tuition_out_of_state)}</div>
        </div>
      </div>
      
      <div className="flex items-center gap-2 mt-3 text-xs text-slate-300">
        <Users className="w-3 h-3" />
        <span>{college.size?.toLocaleString() || 'N/A'} students</span>
        <span>•</span>
        <span className={`px-1.5 py-0.5 rounded ${
          college.ownership === 'public' ? 'bg-green-500/20 text-green-400' :
          college.ownership === 'private_nonprofit' ? 'bg-purple-500/20 text-purple-400' :
          'bg-amber-500/20 text-amber-400'
        }`}>
          {college.ownership === 'public' ? 'Public' : 
           college.ownership === 'private_nonprofit' ? 'Private Non-Profit' : 
           'Private For-Profit'}
        </span>
      </div>
    </motion.div>
  );
};

// College Details Panel
const CollegeDetailsPanel = ({ college, isInState, onInStateChange }) => {
  if (!college) return null;
  
  const tuition = isInState ? college.tuition_in_state : college.tuition_out_of_state;
  const totalCost = tuition ? tuition * 4 * 1.15 : null;
  
  return (
    <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/30">
      <h4 className="text-white font-semibold text-lg mb-3">{college.name}</h4>
      
      <div className="space-y-4">
        {/* Residency Toggle */}
        <div className="flex items-center gap-3">
          <Label className="text-slate-300 text-sm">Residency:</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant={isInState ? "default" : "outline"}
              onClick={() => onInStateChange(true)}
              className={isInState ? "bg-blue-600" : ""}
            >
              In-State
            </Button>
            <Button
              type="button"
              size="sm"
              variant={!isInState ? "default" : "outline"}
              onClick={() => onInStateChange(false)}
              className={!isInState ? "bg-blue-600" : ""}
            >
              Out-of-State
            </Button>
          </div>
        </div>
        
        {/* Cost Summary */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-800/50 rounded-lg p-3">
            <div className="text-slate-300 text-xs mb-1">Annual Tuition</div>
            <div className="text-white font-semibold text-lg">{formatCurrency(tuition)}</div>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3">
            <div className="text-slate-300 text-xs mb-1">Est. 4-Year Cost</div>
            <div className="text-emerald-400 font-semibold text-lg">{formatCurrency(totalCost)}</div>
          </div>
        </div>
        
        {/* Requirements */}
        <div className="bg-slate-800/50 rounded-lg p-3">
          <div className="text-slate-300 text-xs mb-2">Admission Requirements</div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-300">Admission Rate:</span>
              <span className="text-white font-medium">{formatPercent(college.admission_rate)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-300">Avg SAT:</span>
              <span className="text-white font-medium">{college.sat_avg || 'N/A'}</span>
            </div>
          </div>
        </div>
        
        {/* Outcomes */}
        {(college.median_earnings_10yr || college.median_debt) && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
            <div className="text-emerald-400 text-xs mb-2 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              Graduate Outcomes
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {college.median_earnings_10yr && (
                <div className="flex justify-between">
                  <span className="text-slate-300">Median Earnings:</span>
                  <span className="text-emerald-400 font-medium">{formatCurrency(college.median_earnings_10yr)}/yr</span>
                </div>
              )}
              {college.median_debt && (
                <div className="flex justify-between">
                  <span className="text-slate-300">Median Debt:</span>
                  <span className="text-white font-medium">{formatCurrency(college.median_debt)}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Component: College Search and Selection
export default function CollegeSearchSelector({ 
  onSelect, 
  selectedCollege = null,
  showLockOptions = true,
  rank = 1 
}) {
  const { getAuthHeader } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedResult, setSelectedResult] = useState(selectedCollege);
  const [isInState, setIsInState] = useState(true);
  const [major, setMajor] = useState('');
  const [enrollmentYear, setEnrollmentYear] = useState(new Date().getFullYear() + 1);
  const [lockStatus, setLockStatus] = useState('soft_lock');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Debounced search function
  const searchColleges = useCallback(
    debounce(async (query) => {
      if (!query || query.length < 2) {
        setSearchResults([]);
        return;
      }
      
      setIsSearching(true);
      try {
        const response = await axios.post(`${API}/colleges/search`, {
          query,
          limit: 10
        });
        setSearchResults(response.data.results || []);
      } catch (error) {
        console.error('College search error:', error);
        toast.error('Failed to search colleges');
      } finally {
        setIsSearching(false);
      }
    }, 300),
    []
  );
  
  useEffect(() => {
    searchColleges(searchQuery);
  }, [searchQuery, searchColleges]);
  
  const handleSelectCollege = (college) => {
    setSelectedResult(college);
    setSearchQuery(college.name);
    setSearchResults([]); // Clear search results after selection
  };
  
  const handleSubmit = async () => {
    if (!selectedResult) {
      toast.error('Please select a college');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/paths/select-with-college`,
        {
          path_type: 'college',
          college_id: selectedResult.id,
          institution_name: selectedResult.name,
          major_or_track: major,
          location: `${selectedResult.city}, ${selectedResult.state}`,
          enrollment_year: enrollmentYear,
          duration_years: 4,
          lock_status: lockStatus,
          rank: rank,
          is_in_state: isInState
        },
        { headers: getAuthHeader()?.headers }
      );
      
      toast.success(
        lockStatus === 'hard_lock' 
          ? `🎯 Committed to ${selectedResult.name}! Timeline generated.`
          : `Added ${selectedResult.name} as choice #${rank}`
      );
      
      if (onSelect) {
        onSelect(response.data.path);
      }
    } catch (error) {
      console.error('Path selection error:', error);
      showFriendlyError(error, { fallback: 'Failed to save path' });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="space-y-6" data-testid="college-search-selector">
      {/* Search Input */}
      <div className="relative">
        <Label className="text-white mb-2 block">Search Colleges</Label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Type college name (e.g., Duke, MIT, Stanford...)"
            className="pl-10 bg-slate-800 border-slate-700 text-white"
            data-testid="college-search-input"
          />
          {isSearching && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400 animate-spin" />
          )}
        </div>
        
        {/* Search Results Dropdown */}
        <AnimatePresence>
          {searchResults.length > 0 && !selectedResult && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute z-50 w-full mt-2 max-h-96 overflow-y-auto rounded-xl bg-slate-900 border border-slate-700 shadow-xl"
            >
              <div className="p-2 space-y-2">
                {searchResults.map((college) => (
                  <CollegeSearchCard
                    key={college.id}
                    college={college}
                    onSelect={handleSelectCollege}
                    isSelected={selectedResult?.id === college.id}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Selected College Details */}
      {selectedResult && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <CollegeDetailsPanel 
            college={selectedResult} 
            isInState={isInState}
            onInStateChange={setIsInState}
          />
          
          {/* Additional Options */}
          <div className="mt-4 space-y-4">
            {/* Major */}
            <div>
              <Label className="text-white mb-2 block">Intended Major (Optional)</Label>
              <Input
                value={major}
                onChange={(e) => setMajor(e.target.value)}
                placeholder="e.g., Computer Science, Biology, Business"
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="major-input"
              />
            </div>
            
            {/* Enrollment Year */}
            <div>
              <Label className="text-white mb-2 block">Target Enrollment Year</Label>
              <div className="flex gap-2">
                {[0, 1, 2, 3].map((offset) => {
                  const year = new Date().getFullYear() + offset;
                  return (
                    <Button
                      key={year}
                      type="button"
                      size="sm"
                      variant={enrollmentYear === year ? "default" : "outline"}
                      onClick={() => setEnrollmentYear(year)}
                      className={enrollmentYear === year ? "bg-blue-600" : ""}
                    >
                      {year}
                    </Button>
                  );
                })}
              </div>
            </div>
            
            {/* Lock Status */}
            {showLockOptions && (
              <div>
                <Label className="text-white mb-2 block">Commitment Level</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant={lockStatus === 'soft_lock' ? "default" : "outline"}
                    onClick={() => setLockStatus('soft_lock')}
                    className={`flex items-center gap-2 justify-start ${lockStatus === 'soft_lock' ? 'bg-yellow-600 hover:bg-yellow-700' : ''}`}
                    data-testid="soft-lock-btn"
                  >
                    <Unlock className="w-4 h-4" />
                    <div className="text-left">
                      <div className="font-medium">Soft Lock</div>
                      <div className="text-xs opacity-80">Still exploring</div>
                    </div>
                  </Button>
                  <Button
                    type="button"
                    variant={lockStatus === 'hard_lock' ? "default" : "outline"}
                    onClick={() => setLockStatus('hard_lock')}
                    className={`flex items-center gap-2 justify-start ${lockStatus === 'hard_lock' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                    data-testid="hard-lock-btn"
                  >
                    <Lock className="w-4 h-4" />
                    <div className="text-left">
                      <div className="font-medium">Hard Lock</div>
                      <div className="text-xs opacity-80">Committed!</div>
                    </div>
                  </Button>
                </div>
                
                {lockStatus === 'hard_lock' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-3 p-3 rounded-lg bg-blue-500/20 border border-blue-500/30"
                  >
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-300">
                        <strong>Hard Lock will trigger:</strong>
                        <ul className="mt-1 text-xs space-y-1 text-blue-200">
                          <li>• Application timeline generation</li>
                          <li>• Parent notification</li>
                          <li>• Financial projections update</li>
                          <li>• Scholarship matching</li>
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            )}
            
            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !selectedResult}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              data-testid="save-path-btn"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : lockStatus === 'hard_lock' ? (
                <Lock className="w-4 h-4 mr-2" />
              ) : (
                <Target className="w-4 h-4 mr-2" />
              )}
              {lockStatus === 'hard_lock' ? 'Commit to This Path' : 'Add to My Paths'}
            </Button>
            
            {/* Clear Selection */}
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setSelectedResult(null);
                setSearchQuery('');
              }}
              className="w-full text-slate-300 hover:text-white"
            >
              Select Different College
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
